import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CatalogBrowseQuery, MachineCatalogState, TaperType } from "./models/models";
import catalogApi from "./api/catalogApi";

export const searchCatalogModels = createAsyncThunk(
  "machineCatalog/searchModels",
  async ({
    search,
    categoryCode,
    manufacturerId,
    page,
    pageSize
  }: {
    search?: string;
    categoryCode?: string;
    manufacturerId?: string;
    page?: number;
    pageSize?: number;
  }) => {
    return catalogApi.searchModels(search, categoryCode, manufacturerId, page, pageSize);
  }
);

export const browseCatalogModels = createAsyncThunk(
  "machineCatalog/browseModels",
  async (query: CatalogBrowseQuery) => {
    const { search, categoryCode, manufacturerId, page, pageSize, ...filters } = query;
    return catalogApi.searchModels(
      search,
      categoryCode,
      manufacturerId,
      page,
      pageSize,
      filters
    );
  }
);

export const getCatalogTaperTypes = createAsyncThunk(
  "machineCatalog/getTaperTypes",
  async () => {
    return catalogApi.getTaperTypes();
  }
);

export const getCatalogModelDetail = createAsyncThunk(
  "machineCatalog/getModelDetail",
  async (id: string) => {
    return catalogApi.getModelById(id);
  }
);

export const getCatalogManufacturers = createAsyncThunk(
  "machineCatalog/getManufacturers",
  async ({ search, categoryCode }: { search?: string; categoryCode?: string } = {}) => {
    return catalogApi.getManufacturers(search, categoryCode);
  }
);

export const getCatalogCategories = createAsyncThunk(
  "machineCatalog/getCategories",
  async () => {
    return catalogApi.getCategories();
  }
);

const initialState: MachineCatalogState = {
  searchResults: [],
  searchTotalCount: 0,
  searchLoading: false,
  selectedModel: null,
  selectedModelLoading: false,
  manufacturers: [],
  categories: [],
  taperTypes: [],

  browseResults: [],
  browseTotalCount: 0,
  browseTotalPages: 0,
  browseLoading: false,
};

const machineCatalogSlice = createSlice({
  name: "machineCatalog",
  initialState,
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchTotalCount = 0;
    },
    clearSelectedModel: (state) => {
      state.selectedModel = null;
    },
    clearBrowseResults: (state) => {
      state.browseResults = [];
      state.browseTotalCount = 0;
      state.browseTotalPages = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchCatalogModels.pending, (state) => {
        state.searchLoading = true;
      })
      .addCase(searchCatalogModels.fulfilled, (state, { payload }) => {
        state.searchResults = payload.items;
        state.searchTotalCount = payload.totalCount;
        state.searchLoading = false;
      })
      .addCase(searchCatalogModels.rejected, (state) => {
        state.searchLoading = false;
      });

    builder
      .addCase(browseCatalogModels.pending, (state) => {
        state.browseLoading = true;
      })
      .addCase(browseCatalogModels.fulfilled, (state, { payload }) => {
        state.browseResults = payload.items;
        state.browseTotalCount = payload.totalCount;
        state.browseTotalPages = payload.totalPages;
        state.browseLoading = false;
      })
      .addCase(browseCatalogModels.rejected, (state) => {
        state.browseLoading = false;
      });

    builder
      .addCase(getCatalogModelDetail.pending, (state) => {
        state.selectedModelLoading = true;
      })
      .addCase(getCatalogModelDetail.fulfilled, (state, { payload }) => {
        state.selectedModel = payload;
        state.selectedModelLoading = false;
      })
      .addCase(getCatalogModelDetail.rejected, (state) => {
        state.selectedModelLoading = false;
      });

    builder
      .addCase(getCatalogManufacturers.fulfilled, (state, { payload }) => {
        state.manufacturers = payload;
      });

    builder
      .addCase(getCatalogCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
      });

    builder
      .addCase(getCatalogTaperTypes.fulfilled, (state, { payload }) => {
        state.taperTypes = payload;
      });
  }
});

export const {
  clearSearchResults,
  clearSelectedModel,
  clearBrowseResults,
} = machineCatalogSlice.actions;

export default machineCatalogSlice.reducer;
